<template>
  <div class="min-h-screen flex items-center justify-center px-4 bg-gray-100">
    <div class="text-center">
      <!-- Error Image/Icon -->
      <div class="mb-8">
        <svg
          v-if="status === 404"
          class="mx-auto h-24 w-24 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="1"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
          />
        </svg>
        <svg
          v-else-if="status === 403"
          class="mx-auto h-24 w-24 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="1"
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
          />
        </svg>
        <svg
          v-else
          class="mx-auto h-24 w-24 text-red-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="1"
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </div>

      <!-- Error Code -->
      <h1 class="text-9xl font-bold text-gray-300">{{ status }}</h1>

      <!-- Error Message -->
      <h2 class="mt-4 text-3xl font-bold text-gray-900 sm:text-4xl">
        {{ errorTitle }}
      </h2>

      <p class="mt-4 text-lg text-gray-600 max-w-md mx-auto">
        {{ errorMessage }}
      </p>

      <!-- Action Buttons -->
      <div class="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
        <Link
          :href="route('frontend.home')"
          class="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-maroon-600 hover:bg-maroon-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-maroon-500 transition-colors"
        >
          <svg
            class="mr-2 -ml-1 w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
            />
          </svg>
          Kembali ke Beranda
        </Link>

        <button
          v-if="status === 404 || status === 403"
          @click="$inertia.visit(window.history.back())"
          class="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-maroon-500 transition-colors"
        >
          <svg
            class="mr-2 -ml-1 w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M11 15l-3-3m0 0l3-3m-3 3h8M3 12a9 9 0 1118 0 9 9 0 01-18 0z"
            />
          </svg>
          Kembali
        </button>

        <button
          v-else
          @click="location.reload()"
          class="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-maroon-500 transition-colors"
        >
          <svg
            class="mr-2 -ml-1 w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          Coba Lagi
        </button>
      </div>

      <!-- Additional Links -->
      <div v-if="status === 404 || status === 403" class="mt-8">
        <p class="text-sm text-gray-500">Atau kunjungi halaman:</p>
        <div class="mt-2 flex flex-wrap justify-center gap-4 text-sm">
          <Link
            :href="route('frontend.home')"
            class="text-maroon-600 hover:text-maroon-800 hover:underline"
            >Beranda</Link
          >
          <Link
            :href="route('frontend.news')"
            class="text-maroon-600 hover:text-maroon-800 hover:underline"
            >Berita</Link
          >
          <Link
            :href="route('frontend.dokumen')"
            class="text-maroon-600 hover:text-maroon-800 hover:underline"
            >Dokumen</Link
          >
          <Link
            :href="route('frontend.dosen')"
            class="text-maroon-600 hover:text-maroon-800 hover:underline"
            >Dosen</Link
          >
          <Link
            :href="route('frontend.gallery')"
            class="text-maroon-600 hover:text-maroon-800 hover:underline"
            >Galeri</Link
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { Head, Link } from "@inertiajs/vue3";
import { computed, inject } from "vue";
const route = inject('route');

const props = defineProps({
  status: {
    type: Number,
    default: 404,
  },
  message: {
    type: String,
    default: "",
  },
});

const errorTitle = computed(() => {
  switch (props.status) {
    case 404:
      return "Halaman Tidak Ditemukan";
    case 500:
      return "Server Error";
    case 403:
      return "Akses Ditolak";
    default:
      return "Terjadi Kesalahan";
  }
});

const errorMessage = computed(() => {
  if (props.message) {
    return props.message;
  }

  switch (props.status) {
    case 404:
      return "Maaf, halaman yang Anda cari tidak dapat ditemukan. Mungkin halaman telah dipindahkan atau dihapus.";
    case 500:
      return "Maaf, terjadi kesalahan pada server. Tim kami telah diberi tahu dan sedang menangani masalah ini.";
    case 403:
      return "Maaf, Anda tidak memiliki izin untuk mengakses halaman ini.";
    default:
      return "Terjadi kesalahan yang tidak terduga. Silakan coba lagi nanti.";
  }
});
</script>
